import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { MenuItemCard } from './MenuItemCard';
import styles from './MenuItems.module.css';
import { MenuItem } from 'models/digitalMap';
import { AnimatedButton } from 'components/Map/AnimatedButton';

interface Props {
  onClickItem: (item: MenuItem) => void;
  items: MenuItem[];
  id: string;
  onClickShowMenu: () => void;
}

export const MenuItems = React.forwardRef<HTMLElement, Props>(
  ({ onClickItem, items, id, onClickShowMenu }, ref) => {
    const { t } = useTranslation();

    // Add overflow detection logic
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [hasOverflow, setHasOverflow] = React.useState(true);
    React.useEffect(() => {
      const el = containerRef.current;
      if (el) {
        setHasOverflow(el.scrollWidth > el.clientWidth);
      }
    }, [items]);

    const isSingle = items.length === 1;
    const isDoubleWide = items.length === 2 && !hasOverflow;

    return (
      <section ref={ref} id={id} className={styles.section}>
        <div className={styles.btnContainer}>
          <div className={styles.recommendedBadge}>
            {t('Recommended')}
            <img src="/static/images/map/star_blue.svg" alt="Recommended" />
          </div>
          <AnimatedButton
            className={styles.btn}
            clickedClassName={styles.clicked}
            onClick={() => {
              onClickShowMenu();
            }}
          >
            {t('See full menu')}
          </AnimatedButton>
        </div>
        <div className={clsx(styles.itemsContainer, isSingle && styles.single)} ref={containerRef}>
          {items.map((item) => {
            return (
              <MenuItemCard
                item={item}
                isSingle={isSingle}
                isDoubleWide={isDoubleWide}
                key={item.name}
                onClick={() => onClickItem(item)}
              />
            );
          })}
        </div>
      </section>
    );
  }
);

MenuItems.displayName = 'MenuItems';
